"use client";

import { useParams } from "next/navigation";
import { useEffect, useState } from "react";
import Link from "next/link";
import { fetchJob } from "../../lib/api";
import type { JobState } from "../../lib/audit-types";
import { resolveShareToken } from "../../lib/share";
import { CanvasViewer } from "../../(app)/audit/[id]/canvas-viewer";
import { DEMO_JOB_IDS } from "../../constants";

type SharedState =
  | { status: "loading" }
  | { status: "error"; message: string }
  | { status: "ready"; job: JobState };

export function SharedAuditClient() {
  const params = useParams<{ token: string }>();
  const [state, setState] = useState<SharedState>({ status: "loading" });

  useEffect(() => {
    if (!params.token) return;
    const jobId = resolveShareToken(params.token);
    let cancelled = false;

    fetchJob(jobId)
      .then((job) => {
        if (cancelled) return;
        if (job.status !== "done") {
          setState({
            status: "error",
            message: "This audit is still processing. Check back soon.",
          });
          return;
        }
        // Curated gallery demos aren't flagged is_demo in the production
        // DB (live /api/demos returns []), so the canvas labeled them
        // "Shared audit". Patch the flag from the same curated id list the
        // gallery uses until the rows are flagged via setJobDemo.
        setState({
          status: "ready",
          job:
            !job.isDemo && DEMO_JOB_IDS.includes(job.id)
              ? { ...job, isDemo: true }
              : job,
        });
      })
      .catch((err) => {
        if (cancelled) return;
        // 403 = owner is on free plan; share links are Pro/Agency-only.
        // 404 = audit deleted / wrong id.
        const status = (err as { status?: number })?.status;
        setState({
          status: "error",
          message:
            status === 403
              ? "This audit isn't available publicly. The owner needs a Pro or Agency plan to share audits."
              : "This audit could not be found or the link has expired.",
        });
      });

    return () => { cancelled = true; };
  }, [params.token]);

  // Lock body scroll while the canvas is on screen — the canvas is
  // position:fixed and full-viewport, so any background scroll would just
  // reveal a sliver of the page below. Mirrors what the owner audit page
  // does in audit-detail-client.tsx.
  const showingCanvas = state.status === "ready";
  useEffect(() => {
    if (!showingCanvas) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    document.body.dataset.canvasMode = "true";
    return () => {
      document.body.style.overflow = previousOverflow;
      delete document.body.dataset.canvasMode;
    };
  }, [showingCanvas]);

  if (state.status === "loading") {
    return (
      <div className="shared-audit-shell">
        <div className="shared-audit-loading">
          <div className="shared-audit-spinner" />
          <p>Loading shared audit…</p>
        </div>
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="shared-audit-shell">
        <div className="shared-audit-error">
          <h2>Audit unavailable</h2>
          <p>{state.message}</p>
          <Link href="/" className="shared-audit-home-link">
            Go to Snap Site
          </Link>
        </div>
      </div>
    );
  }

  // Ready — render the same canvas the audit owner sees, in read-only mode.
  // The annotations API is public so user-authored pins/callouts come along
  // for the ride. CanvasViewer respects the `data-theme` attribute set by
  // the existing theme toggle (rendered inside the viewer top bar) so
  // light/dark mode works out of the box.
  return <CanvasViewer job={state.job} mode="viewer" canShare={false} />;
}
